import React, { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useMediaQuery } from 'react-responsive'
import Link from 'next/link'
import { searchVehicles, searchVehiclesKey } from '../../../api-client/search'
import useFavorites from '../../../hooks/use-favorites'
import { useAuth } from '../../../contexts/auth'
import { ROUTES } from '../../../constants'
import { now } from '../../../utils/date-service'
import useOnScreen from '../../../hooks/useOnScreen'
import { AuctionStatusRibbon, GridItem } from '../../../components/car-item'
import Loader from '../../../components/loader'

// ----------------------------------------------------------------------
const PAGE_SIZE_MOBILE = 4
const PAGE_SIZE_DESKTOP = 8

const SEARCH_OPTIONS = {
  sortBy: { fieldName: 'auctionTime', sortOrder: 'Ascending' },
  filterBy: {
    multiOptions: [
      {
        fieldName: '',
        values: [],
      },
    ],
    ranges: [
      {
        fieldName: 'year',
        from: '0',
        to: `${now().getFullYear() + 1}`,
      },
      {
        fieldName: 'startingPrice',
        from: '100',
        to: '250000',
      },
      {
        fieldName: 'mileage',
        from: '0',
        to: '200000',
      },
      {
        fieldName: 'mPGCity',
        from: '0',
        to: '150',
      },
    ],
  },
  distance: 250,
  pageIndex: 0,
  excludeCompleted: true,
}

const distinctArrayByProp = (arrayValues, prop) => {
  const result = []
  const map = new Map()
  for (const item of arrayValues) {
    if (!map.has(item[prop])) {
      map.set(item[prop], true) // set any value to Map
      result.push(item)
    }
  }
  return result
}

// ----------------------------------------------------------------------

export default function RecentListingsSection() {
  const isMobile = useMediaQuery({ query: '(max-width: 1024px)' })
  const pageSize = isMobile ? PAGE_SIZE_MOBILE : PAGE_SIZE_DESKTOP
  const { favorites, toggleFavorite, favoritesProcessing } = useFavorites()
  const [{ isAuthenticated, userDetails }] = useAuth()
  const [searchedVehiclesState, setSearchedVehiclesState] = useState([])

  const auctionSectionRef = useRef()
  const isSectionVisible = useOnScreen({ target: auctionSectionRef })

  const { isLoading, isSuccess } = useQuery({
    queryKey: [searchVehiclesKey, pageSize],
    queryFn: () =>
      searchVehicles({
        ...SEARCH_OPTIONS,
        pageSize,
        location: userDetails.location
          ? { latitude: userDetails.location.lat, longitude: userDetails.location.lng }
          : null,
      }),
    enabled: isSectionVisible,
    onSuccess: (data) => {
      if (!data || !data.vehicles) {
        return
      }
      setSearchedVehiclesState((prev) => distinctArrayByProp([...prev, ...data.vehicles], 'id'))
    },
  })

  const showLoader = isLoading || (isAuthenticated && !userDetails.location)
  const showEmptyStateMessage = isSuccess && searchedVehiclesState.length === 0

  return (
    <div className='container' ref={auctionSectionRef}>
      <div className='headings-wrapper'>
        <div className='headings'>
          <h4>Recent listings</h4>
          <Link legacyBehavior href={ROUTES.Search}>
            <a className='flex alignCenter'>View All</a>
          </Link>
        </div>
      </div>

      <div className='auctionSection'>
        {showLoader && (
          <div className='center flex justifyCenter'>
            <Loader />
          </div>
        )}

        {showEmptyStateMessage && (
          <div className='center flex justifyCenter'>
            <p className='noAuctionMessage'>No vehicles available near you</p>
          </div>
        )}

        <div className='vehicles'>
          {searchedVehiclesState?.map((vehicle) => {
            const itemProps = {
              key: vehicle.id,
              id: vehicle.id,
              name: vehicle.name,
              trim: vehicle.trim,
              imageFileKey: vehicle.mainPhotoUrl,
              price: vehicle.price,
              mileage: vehicle.mileage,
              isFavorite: !!favorites[vehicle.id]?.isFavorite,
              onFavorite: toggleFavorite,
              isFavoriteProcessing: !!favoritesProcessing[vehicle.id]?.isProcessing,
              leaseAmount:
                vehicle.paymentDetails?.leaseMonthlyPayment !==
                  vehicle.paymentDetails?.loanMonthlyPayment &&
                vehicle.paymentDetails?.leaseMonthlyPayment,
              financeAmount: vehicle.paymentDetails?.loanMonthlyPayment,
              compareIndex: 0,
              href: isAuthenticated ? `/vehicle/${vehicle.id}` : ROUTES.Search,
              ribbon: (
                <AuctionStatusRibbon
                  status={vehicle.status}
                  auctionStartDate={vehicle.auctionStartDate}
                  timeZone={vehicle.timeZone}
                />
              ),
              year: vehicle.year,
            }

            return <GridItem {...itemProps} />
          })}
        </div>
      </div>

      <style jsx>{`
        .headings-wrapper {
          display: flex;
          padding: 0 5.5rem 2.6rem 4.5rem;
          background-color: #212529;
          padding-top: 7rem;

          .headings {
            max-width: 192rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin: 0 auto;
            width: 100%;

            h4 {
              line-height: 4.3rem;
              font-size: 3.2rem;
              color: #fff;
            }

            a {
              line-height: 2.9rem;
              font-size: 2.2rem;
              color: #fff;
            }
          }
        }

        .auctionSection {
          padding: 4.8rem 3.3rem;
          background: var(--secondary-input-bg);
          position: relative;
          min-height: 45rem;

          .noAuctionMessage {
            line-height: 4.3rem;
            font-size: 3.2rem;
            color: var(--primary-bg-color);
          }

          .vehicles {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            grid-gap: 3.1rem 2.1rem;
            width: 100%;
            max-width: 192rem;
            margin: 0 auto;
          }

          .center {
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
          }
        }

        @media (max-width: 1536px) {
          .auctionSection {
            .vehicles {
              grid-template-columns: repeat(3, 1fr);
            }
          }
        }

        @media (max-width: 1200px) {
          .auctionSection {
            .vehicles {
              display: grid;
              grid-gap: 2rem;
              grid-template-rows: 1fr;
              overflow-x: scroll;
              overflow-y: hidden;
              scroll-snap-type: x proximity;
              padding-bottom: 1rem;
            }
          }
        }

        @media (max-width: 1024px) {
          .container {
            position: relative;
          }

          .headings-wrapper {
            padding: 1.5rem;

            .headings {
              flex-direction: column;

              justify-content: space-between;
              align-items: center;
              margin: 0 auto;

              h4 {
                line-height: 2.6rem;
                font-size: 2rem;
              }

              a {
                line-height: 1.6rem;
                font-size: 1.2rem;
              }
            }
          }

          .auctionSection {
            min-height: 25rem;
            padding: 4.1rem 1rem;

            .noAuctionMessage {
              line-height: 2.6rem;
              font-size: 2rem;
            }

            .vehicles {
              grid-gap: 1rem;
            }
          }
        }
      `}</style>

      <style jsx>{`
        @media (min-width: 1025px) and (max-width: 1200px) {
          .auctionSection {
            .vehicles {
              /* desktop-ish carousel width bucket */
              grid-template-columns: repeat(8, minmax(44.4rem, 1fr));
            }
          }
        }

        @media (max-width: 1024px) {
          .auctionSection {
            .vehicles {
              /* mobile bucket */
              grid-template-columns: repeat(4, minmax(32.4rem, 1fr));
            }
          }
        }
      `}</style>
    </div>
  )
}
